import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MapComponent from './MapComponent';
import { OFFICE_LOCATION } from './LogisticsUtils';
import { LiveKpiCard, RouteLiveListCard, Icon } from './LiveMonitorCards';
import { getLiveRoutesSnapshot } from './liveMonitorUtils';

const LiveMonitorView = ({
  activeRoutes,
  loading,
  error,
  onSelectRoute,
  onOpenRoute,
  onGoToLogistics,
  officeLocation = OFFICE_LOCATION,
  tutorialActive = false,
  tutorialFocusTarget = null
}) => {
  const liveRoutes = useMemo(
    () => getLiveRoutesSnapshot(activeRoutes || [], officeLocation),
    [activeRoutes, officeLocation]
  );

  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [expandedRouteId, setExpandedRouteId] = useState(null);

  useEffect(() => {
    if (!liveRoutes.length) {
      setSelectedRouteId(null);
      setExpandedRouteId(null);
      return;
    }

    if (!selectedRouteId || !liveRoutes.some((route) => route.id === selectedRouteId)) {
      setSelectedRouteId(liveRoutes[0].id);
    }

    if (expandedRouteId && !liveRoutes.some((route) => route.id === expandedRouteId)) {
      setExpandedRouteId(null);
    }
  }, [liveRoutes, selectedRouteId, expandedRouteId]);

  useEffect(() => {
    if (!tutorialActive || !liveRoutes.length) return;

    const firstRouteId = liveRoutes[0]?.id;
    if (!firstRouteId) return;

    if (!selectedRouteId || !liveRoutes.some((route) => route.id === selectedRouteId)) {
      setSelectedRouteId(firstRouteId);
    }

    const shouldExpandFirstCard = [
      '#monitor-global-list',
      '#monitor-global-history-detail',
      '#monitor-global-route-sheet-toggle',
      '#monitor-global-route-sheet',
      '#monitor-global-open-individual'
    ].includes(tutorialFocusTarget);

    if (shouldExpandFirstCard) {
      setExpandedRouteId(firstRouteId);
      setSelectedRouteId(firstRouteId);
    }
  }, [liveRoutes, selectedRouteId, tutorialActive, tutorialFocusTarget]);

  const handleSelectRoute = useCallback((routeId) => {
    setSelectedRouteId(routeId);
  }, []);

  const handleToggleExpand = useCallback((routeId) => {
    setSelectedRouteId(routeId);
    setExpandedRouteId((current) => (current === routeId ? null : routeId));
  }, []);

  const selectedRoute = useMemo(
    () => liveRoutes.find((route) => route.id === selectedRouteId) || liveRoutes[0] || null,
    [liveRoutes, selectedRouteId]
  );

  const movingCount = liveRoutes.filter((route) => route.liveStatusKey === 'moving').length;
  const arrivingCount = liveRoutes.filter((route) => route.liveStatusKey === 'arriving').length;
  const withGpsCount = liveRoutes.filter((route) => !!route.currentLocation).length;
  const staleCount = liveRoutes.filter((route) => route.liveStatusKey === 'stale').length;
  const desktopPanelHeightClass = 'xl:h-[calc(100dvh-24rem)] xl:max-h-[calc(100dvh-24rem)]';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Icon
          path="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0011.667 0l3.181-3.183m-4.991-2.691v4.992h4.992"
          className="w-12 h-12 text-blue-500 animate-spin"
        />
      </div>
    );
  }

  if (error) {
    return <div className="text-center text-red-500 p-8">{error}</div>;
  }

  if (!activeRoutes || activeRoutes.length === 0) {
    return (
      <div id="monitor-global-empty" className="flex flex-col items-center justify-center h-full text-center p-8 bg-white rounded-xl shadow-lg">
        <Icon
          path="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
          className="w-16 h-16 text-slate-400 mb-4"
        />
        <h3 className="text-2xl font-bold text-slate-700">No hay rutas activas para hoy.</h3>
        <p className="mt-2 max-w-xl text-slate-500">
          El monitor se activa cuando existe una hoja de ruta viva. Primero necesitás crear una unidad de transporte y después una ruta con al menos un destino dentro de Logística.
        </p>
        <div id="monitor-global-empty-actions" className="mt-5 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={onGoToLogistics}
            className="inline-flex items-center gap-2 rounded-2xl border border-cyan-300 bg-cyan-50 px-4 py-2 text-sm font-semibold text-cyan-800 transition hover:-translate-y-0.5 hover:bg-cyan-100"
          >
            <Icon path="M11.25 9l3 3-3 3m-6-3h9m6.75 0a8.25 8.25 0 11-16.5 0 8.25 8.25 0 0116.5 0z" className="h-4 w-4" />
            Ir a Logística
          </button>
          <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
            Activá una ruta y este módulo se completa solo
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 flex flex-col gap-4 overflow-x-hidden">
      <div id="monitor-global-kpis" className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <LiveKpiCard title="Rutas activas" value={liveRoutes.length} helper="Operación viva del día" iconPath="M3.75 12h16.5m-16.5 0a8.25 8.25 0 1116.5 0m-16.5 0l3.75 3.75m12.75-3.75l-3.75 3.75" tone="blue" />
        <LiveKpiCard title="Unidades con GPS" value={withGpsCount} helper={`${Math.max(liveRoutes.length - withGpsCount, 0)} sin posición`} iconPath="M15 10.5a3 3 0 11-6 0 3 3 0 016 0zM19.5 10.5c0 7.142-7.5 12-7.5 12s-7.5-4.858-7.5-12a7.5 7.5 0 1115 0z" tone="emerald" />
        <LiveKpiCard title="En movimiento" value={movingCount} helper={`${arrivingCount} con arribo próximo`} iconPath="M13.5 4.5L21 12l-7.5 7.5M3 12h18" tone="violet" />
        <LiveKpiCard title="Sin señal reciente" value={staleCount} helper="Revisar actualización de unidades" iconPath="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zM12 16.5h.008v.008H12V16.5z" tone="amber" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 flex-grow min-h-0 xl:items-start xl:overflow-hidden">
        <div id="monitor-global-map" className={`xl:col-span-8 min-h-[280px] h-[34vh] md:h-[38vh] ${desktopPanelHeightClass} relative z-0 self-start xl:sticky xl:top-0`}>
          <div className="h-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <MapComponent
              officeLocation={officeLocation}
              liveRoutes={liveRoutes}
              selectedRouteId={selectedRoute?.id || null}
              focusedRouteId={expandedRouteId || null}
              onRouteSelect={handleSelectRoute}
            />
          </div>
        </div>

        <div id="monitor-global-list" className={`xl:col-span-4 flex flex-col min-h-0 self-stretch ${desktopPanelHeightClass}`}>
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm flex-1 min-h-0 overflow-hidden flex flex-col">
            <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50">
              <div className="flex items-center justify-between gap-3">
                <h3 className="font-bold text-slate-800 text-sm tracking-[0.01em]">Flota activa</h3>
                <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-slate-900 text-white text-[10px] font-black uppercase tracking-[0.14em]">{liveRoutes.length} unidades</span>
              </div>
              <p className="mt-1 text-[11px] leading-relaxed text-slate-500">Tocá cualquier barra para abrir o cerrar su detalle. El botón “Ver” hace la misma acción de forma más explícita.</p>
            </div>

            <div className="p-3 space-y-2 flex-1 min-h-0 overflow-y-auto overscroll-contain touch-pan-y" style={{ WebkitOverflowScrolling: 'touch' }}>
              {liveRoutes.map((route) => (
                <RouteLiveListCard
                  key={route.id}
                  route={route}
                  openRouteButtonId={route.id === liveRoutes[0]?.id ? 'monitor-global-open-route' : undefined}
                  detailButtonId={route.id === liveRoutes[0]?.id ? 'monitor-global-history-detail' : undefined}
                  manifestToggleButtonId={route.id === liveRoutes[0]?.id ? 'monitor-global-route-sheet-toggle' : undefined}
                  manifestContainerId={route.id === liveRoutes[0]?.id ? 'monitor-global-route-sheet' : undefined}
                  manifestOpenRouteButtonId={route.id === liveRoutes[0]?.id ? 'monitor-global-open-individual' : undefined}
                  selected={route.id === selectedRoute?.id}
                  expanded={route.id === expandedRouteId}
                  onSelect={handleSelectRoute}
                  onToggleExpand={handleToggleExpand}
                  onOpenDetail={onSelectRoute}
                  onOpenRoute={onOpenRoute}
                  tutorialActive={tutorialActive}
                  tutorialFocusTarget={tutorialFocusTarget}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveMonitorView;
